import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "../../styles/hc-modal.css";

/* ============================================================================
   Piezas de los modales de Historia Clínica.

   Existen para que el markup no se repita ocho veces: `Section` reemplaza los
   acordeones numerados y las cabeceras `bg-primary`; `Segmented` reemplaza los
   grupos de toggles que hoy permiten estados imposibles.
   ========================================================================== */

/* --------------------------------------------------------------- sección --- */
export function Section({ title, hint, children, id }) {
  return (
    <section className="hcs" id={id}>
      <div className="hcs__head">
        <h3 className="hcs__title">{title}</h3>
        {hint && <span className="hcs__hint">{hint}</span>}
      </div>
      {children}
    </section>
  );
}

/* ----------------------------------------------------------------- campo --- */
export function Field({ label, required, hint, error, htmlFor, children, className = "" }) {
  return (
    <div className={"hcf " + className}>
      {label && (
        <label className="hcf__label" htmlFor={htmlFor}>
          {label}
          {required && <span className="hcf__req" aria-hidden="true"> *</span>}
          {required && <span className="visually-hidden"> (obligatorio)</span>}
        </label>
      )}
      {children}
      {error ? (
        <div className="hcf__error" role="alert">
          <IconoAlerta size={13} />
          {error}
        </div>
      ) : (
        hint && <div className="hcf__hint">{hint}</div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------- segmented ---
   Radiogroup con aspecto de segmented control. Reemplaza los grupos de
   toggles independientes: aquí es imposible marcar dos a la vez. */
export function Segmented({ options, value, onChange, name, ariaLabel }) {
  return (
    <div className="hcseg" role="radiogroup" aria-label={ariaLabel || name}>
      {options.map((o) => {
        const val = o.value ?? o;
        const label = o.label ?? o;
        const activo = value === val;
        return (
          <button
            key={val}
            type="button"
            role="radio"
            aria-checked={activo}
            className={"hcseg__opt " + (o.peligro ? "hcseg__opt--peligro" : "")}
            onClick={() => onChange(val)}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------- chip group --- */
export function ChipGroup({ options, value, onChange, multi = false, ariaLabel }) {
  const seleccion = multi ? value || [] : value;
  const activo = (val) => (multi ? seleccion.includes(val) : seleccion === val);
  const alternar = (val) => {
    if (!multi) return onChange(seleccion === val ? null : val);
    return onChange(seleccion.includes(val) ? seleccion.filter((x) => x !== val) : [...seleccion, val]);
  };
  return (
    <div className="hcchips" role="group" aria-label={ariaLabel}>
      {options.map((o) => {
        const val = o.value ?? o;
        const label = o.label ?? o;
        return (
          <button
            key={val}
            type="button"
            aria-pressed={activo(val)}
            className="hcchip"
            onClick={() => alternar(val)}
          >
            {activo(val) && <IconoCheck size={12} />}
            {label}
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------ casillas en fila --- */
export function CheckInline({ items, onToggle, ariaLabel }) {
  return (
    <div className="hcinline" role="group" aria-label={ariaLabel}>
      {items.map((it) => (
        <label key={it.id ?? it.value} className={"hccheck " + (it.checked ? "hccheck--on" : "")}>
          <input type="checkbox" checked={!!it.checked} onChange={(e) => onToggle(it, e.target.checked)} />
          {it.label}
        </label>
      ))}
    </div>
  );
}

/* --------------------------------------------------- nº de orden (badge) ---
   Solo lectura por definición: el correlativo lo compone el servidor. */
export function OrderBadge({ code, label = "N.º de orden" }) {
  if (!code) return null;
  return (
    <span className="hcm__badge hcm__badge--orden" title={label}>
      {code}
    </span>
  );
}

export function DateBadge({ value, label = "Fecha" }) {
  if (!value) return null;
  return (
    <span className="hcm__badge" title={label}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M8 3v4M16 3v4M3 10h18" />
      </svg>
      {value}
    </span>
  );
}

/* ------------------------------------------------------- vista de impresión --- */
export function PrintPreview({ children, label = "Así se imprime" }) {
  return (
    <div className="hcprev">
      <div className="hcprev__label">{label}</div>
      {children}
    </div>
  );
}

/* --------------------------------------------- lista de exámenes (multi) ---
   Buscador + dos columnas + checkboxes. Sustituye a los toggles con límite
   de "un solo examen". */
export function ExamenPicker({ items, seleccionados, onToggle, placeholder = "Buscar…", vacio = "Sin resultados." }) {
  const [q, setQ] = useState("");
  const filtrados = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return items;
    return items.filter((x) => `${x.codigo || ""} ${x.descripcion || ""}`.toLowerCase().includes(t));
  }, [items, q]);

  return (
    <div className="hclist">
      <div className="hclist__buscar">
        <input
          type="text"
          value={q}
          placeholder={placeholder}
          onChange={(e) => setQ(e.target.value)}
          aria-label={placeholder}
        />
      </div>
      <div className="hclist__grid">
        {filtrados.length === 0 ? (
          <div className="hclist__vacio">{vacio}</div>
        ) : (
          filtrados.map((x) => {
            const on = seleccionados.includes(x.IdExamenTipo);
            return (
              <label key={x.IdExamenTipo} className={"hclist__item " + (on ? "hclist__item--on" : "")}>
                <input type="checkbox" checked={on} onChange={() => onToggle(x.IdExamenTipo)} />
                <span>{x.descripcion}</span>
              </label>
            );
          })
        )}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------- autocomplete ---
   Buscador con lista desplegable, navegable con teclado y que confirma con
   Enter. Lo comparten el CIE-10 y el vademécum: mismo gesto en los dos, y una
   pulsación en vez de abrir-select-elegir.

   `onBuscar(texto)` puede ser sincrónico (filtrar una lista ya cargada) o
   devolver una promesa (buscar en el servidor). Si `permitirLibre` está activo,
   ofrece usar lo escrito tal cual -- es como se registra un medicamento que no
   está en catálogo. */
export function Autocomplete({
  valor,
  onElegir,
  onBuscar,
  placeholder,
  permitirLibre = false,
  textoLibre = (t) => `Usar «${t}» como texto libre`,
  minimo = 2,
  id,
  ariaInvalid,
  renderOpcion,
  vacio = "Sin resultados",
}) {
  const [texto, setTexto] = useState("");
  const [opciones, setOpciones] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const [cursor, setCursor] = useState(-1);
  const [buscando, setBuscando] = useState(false);
  const pedidoRef = useRef(0);
  const cajaRef = useRef(null);
  const [rect, setRect] = useState(null);

  /* El cuerpo del modal tiene `overflow-y: auto`, así que un desplegable
     posicionado dentro queda RECORTADO por él -- se veía una franja de dos
     filas. Se saca a un portal en <body> con posición fija, midiendo el input,
     y se re-mide mientras está abierto (scroll y resize incluidos). */
  useEffect(() => {
    if (!abierto) return undefined;
    const medir = () => {
      const el = cajaRef.current;
      if (el) setRect(el.getBoundingClientRect());
    };
    medir();
    window.addEventListener("scroll", medir, true);
    window.addEventListener("resize", medir);
    return () => {
      window.removeEventListener("scroll", medir, true);
      window.removeEventListener("resize", medir);
    };
  }, [abierto, opciones.length]);

  useEffect(() => {
    const t = texto.trim();
    if (t.length < minimo) {
      setOpciones([]);
      setAbierto(false);
      return undefined;
    }
    const pedido = ++pedidoRef.current;
    setBuscando(true);
    const temporizador = setTimeout(async () => {
      const res = await Promise.resolve(onBuscar(t));
      // Una respuesta que llega tarde no debe pisar a una búsqueda más nueva.
      if (pedido !== pedidoRef.current) return;
      setOpciones(res || []);
      setCursor((res || []).length > 0 ? 0 : -1);
      setAbierto(true);
      setBuscando(false);
    }, 350);
    return () => clearTimeout(temporizador);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [texto, minimo]);

  const elegir = (op) => {
    onElegir(op);
    setTexto("");
    setOpciones([]);
    setAbierto(false);
  };

  const alPulsar = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, opciones.length - 1 + (permitirLibre ? 1 : 0)));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (cursor >= 0 && cursor < opciones.length) elegir(opciones[cursor]);
      else if (permitirLibre && texto.trim()) elegir({ libre: true, label: texto.trim(), value: texto.trim() });
    } else if (e.key === "Escape" && abierto) {
      e.stopPropagation(); // no cerrar el modal, solo la lista
      setAbierto(false);
    }
  };

  // Ya hay algo elegido: se muestra como tarjeta con ✕ para cambiarlo.
  if (valor) {
    return (
      <div className="hcauto__sel">
        <div className="hcauto__sel-txt">
          {renderOpcion ? renderOpcion(valor) : valor.label}
        </div>
        {valor.libre && <span className="hcm__badge">Fuera de catálogo</span>}
        <button type="button" className="hcauto__quitar" aria-label="Cambiar" onClick={() => onElegir(null)}>
          ✕
        </button>
      </div>
    );
  }

  const lista = (
        <div
          className="hcauto__lista"
          role="listbox"
          style={
            rect
              ? { position: "fixed", top: rect.bottom + 3, left: rect.left, width: rect.width }
              : undefined
          }
        >
          {opciones.map((op, i) => (
            <button
              key={op.value ?? i}
              type="button"
              role="option"
              aria-selected={cursor === i}
              className={"hcauto__op " + (cursor === i ? "hcauto__op--cursor" : "")}
              onMouseEnter={() => setCursor(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => elegir(op)}
            >
              {renderOpcion ? renderOpcion(op) : op.label}
            </button>
          ))}
          {opciones.length === 0 && !buscando && <div className="hcauto__vacio">{vacio}</div>}
          {buscando && <div className="hcauto__vacio">Buscando…</div>}
          {permitirLibre && texto.trim() && (
            <button
              type="button"
              className={"hcauto__op hcauto__op--libre " + (cursor === opciones.length ? "hcauto__op--cursor" : "")}
              onMouseEnter={() => setCursor(opciones.length)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => elegir({ libre: true, label: texto.trim(), value: texto.trim() })}
            >
              {textoLibre(texto.trim())}
            </button>
          )}
        </div>
  );

  return (
    <div className="hcauto" ref={cajaRef}>
      <input
        id={id}
        type="text"
        value={texto}
        placeholder={placeholder}
        aria-invalid={ariaInvalid}
        autoComplete="off"
        onChange={(e) => setTexto(e.target.value)}
        onKeyDown={alPulsar}
        onFocus={() => opciones.length > 0 && setAbierto(true)}
        onBlur={() => setTimeout(() => setAbierto(false), 150)}
      />
      {abierto && createPortal(lista, document.body)}
    </div>
  );
}

/* ------------------------------------------------------------ iconos ------- */
export function IconoAlerta({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v5" />
      <path d="M12 16.5h.01" />
    </svg>
  );
}
export function IconoCheck({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 12.5l5.5 5.5L20 7" />
    </svg>
  );
}
